import { Controller } from "@hotwired/stimulus"
import L from "leaflet"

export default class extends Controller {
  static targets = ["container", "popupTemplate"]
  static values = {
    latitude: Number,
    longitude: Number,
    zoom: { type: Number, default: 13 },
    userInteraction: { type: Boolean, default: true },
    currentLat: Number,
    currentLng: Number,
    mapUrl: String
  }

  connect() {
    console.debug("Map controller connected")
    this.nearbyMarkers = [] // Track nearby discovery markers
    this.mainMarker = null // Track the main discovery marker
    this.initializeMap()
    this.setupTurboFrameListener()
  }

  disconnect() {
    // Clean up event listeners
    document.removeEventListener('turbo:frame-load', this.turboFrameListener)

    // Clean up map if it exists
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  // Called when new thumbnail targets are added (when nearby discoveries load)
  thumbnailTargetConnected(element) {
    this.addNearbyDiscoveryMarker(element)
  }

  setupTurboFrameListener() {
    // Store the listener function so we can remove it later
    this.turboFrameListener = (event) => {
      if (event.target.id === 'nearby-discoveries') {
        console.debug("Nearby discoveries frame loaded, adding markers")
        // Clear existing nearby markers first
        this.clearNearbyMarkers()
        // Small delay to ensure DOM is fully updated
        setTimeout(() => {
          this.addNearbyDiscoveryMarkers()
        }, 100)
      }
    }

    document.addEventListener('turbo:frame-load', this.turboFrameListener)
  }

  initializeMap() {
    console.debug("Map initialization started")
    // Check if map is already initialized
    if (this.map) {
      return
    }

    // Initialize map
    this.map = L.map(this.containerTarget, {
      dragging: this.userInteractionValue,
      touchZoom: this.userInteractionValue,
      scrollWheelZoom: this.userInteractionValue,
      doubleClickZoom: this.userInteractionValue,
      boxZoom: this.userInteractionValue,
      tap: this.userInteractionValue,
      keyboard: this.userInteractionValue
    }).setView([this.latitudeValue, this.longitudeValue], this.zoomValue)

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(this.map)

    // Add click event listener to navigate to map page
    // this.map.on('click', (e) => {
    //   window.location.href = this.mapUrlValue
    // })

    this.showSingleDiscovery()
    // Don't add nearby markers here - they'll be added when turbo frame loads
  }

  showSingleDiscovery() {
    console.debug("Showing single discovery at", this.latitudeValue, this.longitudeValue)

    // Create a marker at the specified location
    this.mainMarker = L.marker([this.latitudeValue, this.longitudeValue]).addTo(this.map)

    // Center the map on the marker
    this.map.setView([this.latitudeValue, this.longitudeValue], this.zoomValue)

    // If we have a popup template, we can use it
    // if (this.hasPopupTemplateTarget) {
    //   const popupTemplate = this.popupTemplateTarget.cloneNode(true)
    //   popupTemplate.classList.remove('is-hidden')
    //   marker.bindPopup(popupTemplate.innerHTML)
    // }
  }

  clearNearbyMarkers() {
    console.debug("Clearing nearby discovery markers")

    // Remove all nearby discovery markers from the map
    this.nearbyMarkers.forEach(marker => {
      if (this.map && marker) {
        this.map.removeLayer(marker)
      }
    })
    this.nearbyMarkers = []
  }

  addNearbyDiscoveryMarkers() {
    // Look for thumbnail elements with discovery-map-target="thumbnail"
    // within the nearby-discoveries frame
    const nearbyFrame = document.getElementById('nearby-discoveries')
    if (!nearbyFrame) {
      console.debug("Nearby discoveries frame not found")
      return
    }

    const thumbnails = nearbyFrame.querySelectorAll('[data-discovery-map-target="thumbnail"]')
    console.debug(`Found ${thumbnails.length} nearby discovery thumbnails`)

    thumbnails.forEach(thumbnail => {
      this.addNearbyDiscoveryMarker(thumbnail)
    })

    // After adding all markers, zoom to fit all markers
    this.zoomToFitAllMarkers()
  }

  addNearbyDiscoveryMarker(thumbnail) {
    if (!this.map) return

    // Get latitude, longitude, and index from thumbnail data attributes
    const lat = parseFloat(thumbnail.dataset.discoveryLatitude)
    const lng = parseFloat(thumbnail.dataset.discoveryLongitude)
    const discoveryIndex = parseInt(thumbnail.dataset.discoveryIndex)

    if (!lat || !lng || !discoveryIndex) return

    console.debug(`Adding nearby discovery marker ${discoveryIndex} at`, lat, lng)

    // Create a custom numbered marker
    const markerIcon = L.divIcon({
      className: 'custom-numbered-marker',
      html: `<div class="marker-number">${discoveryIndex}</div>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    })

    const marker = L.marker([lat, lng], { icon: markerIcon }).addTo(this.map)

    // Add click handler to highlight the corresponding thumbnail
    // marker.on('click', () => {
    //   this.highlightThumbnail(thumbnail)
    // })

    // Store marker reference on the thumbnail for potential cleanup
    thumbnail._mapMarker = marker

    // Track the marker in the nearbyMarkers array
    this.nearbyMarkers.push(marker)
  }

  zoomToFitAllMarkers() {
    if (!this.map) return

    const allMarkers = []

    // Add main marker if it exists
    if (this.mainMarker) {
      allMarkers.push(this.mainMarker)
    }

    // Add all nearby markers
    allMarkers.push(...this.nearbyMarkers)

    if (allMarkers.length === 0) {
      console.debug("No markers to fit bounds to")
      return
    }

    if (allMarkers.length === 1) {
      // If only one marker (main discovery), just center on it with default zoom
      const marker = allMarkers[0]
      this.map.setView(marker.getLatLng(), this.zoomValue)
      return
    }

    // Create a bounds object that includes all markers
    const group = new L.featureGroup(allMarkers)
    const bounds = group.getBounds()

    // Fit the map to show all markers with some padding
    this.map.fitBounds(bounds, {
      padding: [20, 20], // Add 20px padding around the bounds
      maxZoom: 16 // Don't zoom in too close
    })

    console.debug(`Zoomed to fit ${allMarkers.length} markers`)
  }

  highlightThumbnail(thumbnail) {
    // Remove previous highlights from all nearby discovery thumbnails
    const nearbyFrame = document.getElementById('nearby-discoveries')
    if (nearbyFrame) {
      const allThumbnails = nearbyFrame.querySelectorAll('[data-discovery-map-target="thumbnail"]')
      allThumbnails.forEach(t => {
        t.classList.remove('is-highlighted')
      })
    }

    // Add highlight to the clicked thumbnail
    thumbnail.classList.add('is-highlighted')
    thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest' })

    // Remove highlight after a few seconds
    setTimeout(() => {
      thumbnail.classList.remove('is-highlighted')
    }, 3000)
  }
}
